/**
 * Phase 1/7 admin UI — read-side viewer for backend/audit.py's audit_log
 * collection (write_audit() has been recording admin mutations since Phase 1;
 * this is the first place an admin can actually read them back).
 */
import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import { SkeletonTable } from "../components/ui/Skeleton";
import { RefreshCw, ScrollText, ChevronDown, ChevronRight } from "lucide-react";
import { apiListAuditLog } from "../lib/cloudSync";

const AuditLog = () => {
  const [section, setSection] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (sec) => {
    setLoading(true);
    setError("");
    try {
      const data = await apiListAuditLog(sec || undefined, 200);
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.message || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(section); }, [section, load]);

  return (
      <div className="max-w-5xl space-y-6">
          <PageHeader
            title="Audit Log"
            subtitle="Track every admin mutation and live configuration change, append-only."
            actions={
              <button
                onClick={() => load(section)}
                className="text-xs text-celestial-muted hover:text-celestial-text flex items-center gap-1.5 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            }
          />

          <input
            value={section}
            onChange={(e) => setSection(e.target.value)}
            placeholder="Filter by section (e.g. banlists.genesys)…"
            className="w-full px-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50 text-sm"
          />

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{error}</div>
          )}

          {loading ? (
            <SkeletonTable rows={5} />
          ) : (
          <div className="card-celestial rounded-2xl divide-y divide-celestial-border/10">
            {items.length === 0 ? (
              <EmptyState icon={ScrollText} title="No audit entries yet." description="Every admin mutation will be recorded here as it happens." />
            ) : (
              items.map((item) => {
                const isOpen = expanded === item.id;
                return (
                  <div key={item.id}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : item.id)}
                      className="w-full p-4 flex items-center justify-between gap-4 text-left hover:bg-surface/30"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen ? (
                          <ChevronDown className="w-3.5 h-3.5 text-celestial-muted shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-celestial-muted shrink-0" />
                        )}
                        <span className="font-medium text-celestial-text truncate">{item.action}</span>
                        <span className="text-xs text-celestial-muted/70 truncate">{item.section}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-xs text-celestial-muted">
                        <span>{item.admin_email || "unknown"}</span>
                        <span className="text-celestial-muted/60">
                          {item.created_at ? new Date(item.created_at).toLocaleString() : "—"}
                        </span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="text-celestial-muted mb-1">Old value</div>
                          <pre className="bg-surface/50 border border-celestial-border/20 rounded-lg p-2 overflow-auto max-h-64 text-celestial-text/90">
                            {JSON.stringify(item.old_value, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <div className="text-celestial-muted mb-1">New value</div>
                          <pre className="bg-surface/50 border border-celestial-border/20 rounded-lg p-2 overflow-auto max-h-64 text-celestial-text/90">
                            {JSON.stringify(item.new_value, null, 2)}
                          </pre>
                        </div>
                        {item.reason && (
                          <div className="col-span-2 text-celestial-muted">Reason: {item.reason}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          )}
      </div>
  );
};

export default AuditLog;
